import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import dayjs from "dayjs"

interface Item {
  id: number
  name: string
  sell_price: number
  quantity: number
  sku: string
  is_weight: boolean
  stock: number
  discountPercent: number
  discountedPrice: number
  total: number
  note: string
  // Set while the price comes from a catalog discount, so records can show both
  originalPrice?: number | null
  discountLabel?: string | null
}

export interface OrderState {
  customerId: number
  date: string
  totalItems: number
  items: Item[]
  subtotal: number
  change: number
  paymentMethod: number | null
  cashReceived: number | null
  splitBilling: boolean
  splitPayments: { id: number; display_name: string; amount: number }[]
  discountPercent: number // global percent discount (0..100)
  discountAmount: number // global fixed discount (currency)
  totalDiscountAmount: number // sum of all discounts (per-item + global)
  totalAmount: number // final payable amount = subtotal - globalPercentAmount - globalFixed
  note: string
  paymentStatus: number | null
  salesStatus: number | null
}

const toFixedNum = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

/**
 * The website sells at the catalog discount's final price, and so does the POS.
 * It is saved as the item's sell_price, like any other price.
 */
const catalogPrice = (product: {
  sell_price?: number
  discount_details?: {
    has_discount: boolean
    final_price: number
    label: string
  }
}) => {
  const sellPrice = Number(product.sell_price)
  const details = product.discount_details
  const finalPrice = toFixedNum(Number(details?.final_price))

  if (!details?.has_discount || !(finalPrice < sellPrice)) {
    return { price: sellPrice, originalPrice: null, discountLabel: null }
  }

  return {
    price: finalPrice,
    originalPrice: sellPrice,
    discountLabel: details.label,
  }
}

const initialState: OrderState = {
  totalItems: 0,
  customerId: 1,
  date: dayjs().format("YYYY-MM-DD HH:mm:ss"),
  items: [],
  paymentMethod: null,
  paymentStatus: null,
  salesStatus: null,
  change: 0,
  cashReceived: null,
  splitBilling: false,
  splitPayments: [],
  subtotal: 0,
  discountPercent: 0,
  discountAmount: 0,
  totalDiscountAmount: 0,
  totalAmount: 0,
  note: "",
}

/**
 * Recalculate derived values for entire state.
 * - updates each item's discountedPrice and total
 * - computes subtotal (sum of item totals)
 * - computes itemsDiscountSum (sum of per-item discount amounts)
 * - computes globalPercentAmount and uses discountAmount (fixed)
 * - sets totalDiscountAmount and totalAmount
 */
const recalculate = (state: OrderState) => {
  // update items derived fields
  let subtotal = 0
  let itemsDiscountSum = 0
  let totalItems = 0

  state.items = state.items.map((it) => {
    const quantity = Number(it.quantity) || 0
    const sellPrice = Number(it.sell_price) || 0
    const discPct = isNaN(it.discountPercent) ? 0 : Number(it.discountPercent)

    const cappedDiscPct = Math.max(0, Math.min(100, discPct))
    const discountedPrice = sellPrice * (1 - cappedDiscPct / 100)
    const lineTotal = discountedPrice * quantity
    const lineDiscountAmount = sellPrice * quantity - lineTotal

    subtotal += lineTotal
    itemsDiscountSum += lineDiscountAmount
    totalItems += quantity

    return {
      ...it,
      discountedPrice: toFixedNum(discountedPrice),
      total: toFixedNum(lineTotal),
    }
  })

  subtotal = toFixedNum(subtotal)
  const globalPercentAmount = toFixedNum(
    (subtotal * (Number(state.discountPercent) || 0)) / 100
  )
  const globalFixed = toFixedNum(Number(state.discountAmount) || 0)

  // total discount = per-item discounts + global percent amount + global fixed amount
  const totalDiscountAmount = toFixedNum(
    itemsDiscountSum + globalPercentAmount + globalFixed
  )

  // final amount after global discounts (subtotal already excludes per-item discounts)
  const totalAmount = toFixedNum(
    Math.max(0, subtotal - globalPercentAmount - globalFixed)
  )

  state.subtotal = subtotal
  state.totalDiscountAmount = totalDiscountAmount
  state.totalAmount = totalAmount
  state.totalItems = totalItems
}

export const orderFormSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    /**
     * Add item (merge by id). If item exists increment quantity, otherwise add new.
     * Accepts partial item payload: we'll normalize missing derived fields.
     */
    addItem(
      state,
      action: PayloadAction<
        Partial<
          Pick<
            Item,
            "id" | "name" | "sell_price" | "stock" | "sku" | "is_weight"
          >
        > & {
          quantity?: number
          note: string
          discount_details?: {
            has_discount: boolean
            final_price: number
            label: string
          }
        }
      >
    ) {
      const payload = action.payload
      if (!payload.id || payload.sell_price == null) return // invalid payload

      const quantityToAdd = Math.max(1, Number(payload.quantity) || 1)

      const existing = state.items.find((i) => {
        return i.id === payload.id
      })
      if (existing) {
        existing.quantity = existing.quantity + quantityToAdd
      } else {
        const { price, originalPrice, discountLabel } = catalogPrice(payload)
        const newItem: Item = {
          id: payload.id,
          name: payload.name ?? "Unnamed",
          sell_price: price,
          originalPrice,
          discountLabel,
          stock: Number(payload.stock) || 0,
          is_weight: payload.is_weight || false,
          sku: payload.sku ?? "N/A",
          quantity: quantityToAdd,
          discountPercent: 0,
          discountedPrice: price,
          total: toFixedNum(price * quantityToAdd),
          note: payload.note,
        }
        state.items.push(newItem)
      }

      recalculate(state)
    },

    // inside your slice reducers
    setOrderForEdit(
      state,
      action: PayloadAction<any> // you can type this as your API response
    ) {
      const data = action.payload

      state.items = data.order_items.map((it: any) => ({
        id: it.id,
        name: it.name,
        sell_price: Number(it.sell_price),
        originalPrice: it.original_price ?? null,
        discountLabel: it.discount_label ?? null,
        quantity: Number(it.quantity),
        sku: it.sku ?? "N/A",
        is_weight: !!it.is_weight,
        stock: Number(it.stock || 0),
        discountPercent: it.discount,
        discountedPrice: Number(it.sell_price),
        total: Number(it.total),
      }))

      // Set other fields
      state.subtotal = Number(data.total_amount) // optionally recalc if needed
      state.totalAmount = Number(data.total_amount)
      state.totalDiscountAmount = 0
      state.discountAmount = data.discount_amount
      state.discountPercent = data.discount_percentage
      state.cashReceived = data.extras?.cash_received
      state.change = 0

      // customer and date
      state.customerId = data.customer.id
      state.date = data.date
      state.note = data.note

      // Payment
      state.splitBilling = !!(data.split_billing ?? data.is_split_payment) // default false if missing
      state.splitPayments = data.split_payments
        ? data.split_payments.map((p: any) => ({
            id: p.payment_method, // payment_method is the payment method ID
            display_name: p.display_name || p.payment_method_name,
            amount: Number(p.amount),
          }))
        : []

      if (!state.splitBilling) {
        state.paymentMethod = data.payment_method_id || null
      } else {
        state.paymentMethod = null
      }
      state.paymentStatus = data.payment_status_id
      state.salesStatus = data.sales_status_id

      // Recalculate totals
      recalculate(state)
    },

    /**
     * Remove item by id
     */
    removeItem(state, action: PayloadAction<number>) {
      state.items = state.items.filter((i) => i.id !== action.payload)
      recalculate(state)
    },

    /**
     * Clear all items
     */
    clearItems(state) {
      state.items = []
      recalculate(state)
    },

    /**
     * Update item quantity by id
     */
    updateItemQuantity(
      state,
      action: PayloadAction<{ index: number; quantity: number }>
    ) {
      const { index, quantity } = action.payload
      if (quantity <= 0) return
      const item = state.items[index]
      if (!item) return
      item.quantity = Number(quantity)
      recalculate(state)
    },

    updateSellPrice(
      state,
      action: PayloadAction<{ index: number; sellPrice: number }>
    ) {
      const { index, sellPrice } = action.payload

      if (sellPrice < 0) return

      const item = state.items[index]
      if (!item) return
      item.sell_price = Number(sellPrice)
      // A typed price replaces the catalog discount
      item.originalPrice = null
      item.discountLabel = null
      recalculate(state)
    },

    /**
     * Update per-item discount percent by id (0..100)
     */
    updateItemDiscount(
      state,
      action: PayloadAction<{ index: number; discountPercent: number }>
    ) {
      const { index, discountPercent } = action.payload
      const item = state.items[index]
      if (!item) return
      item.discountPercent = Math.max(
        0,
        Math.min(100, Number(discountPercent) || 0)
      )
      recalculate(state)
    },

    /**
     * Update item fully (replace fields) - useful for edit UI
     */
    updateItem(state, action: PayloadAction<Partial<Item> & { id: string }>) {
      const { id, ...rest } = action.payload
      const item = state.items.find((i) => i.id === id)
      if (!item) return
      Object.assign(item, rest)
      // ensure numeric fields
      item.quantity = Number(item.quantity) || 0
      item.sell_price = Number(item.sell_price) || 0
      item.discountPercent = Number(item.discountPercent) || 0
      recalculate(state)
    },

    /**
     * Set global fixed discount amount (currency)
     */
    setGlobalDiscountAmount(state, action: PayloadAction<number>) {
      state.discountAmount = Math.max(0, Number(action.payload) || 0)
      recalculate(state)
    },

    /**
     * Set global discount percent (0..100)
     */
    setGlobalDiscountPercent(state, action: PayloadAction<number>) {
      state.discountPercent = Math.max(
        0,
        Math.min(100, Number(action.payload) || 0)
      )
      recalculate(state)
    },
    setCustomerId(state, action: PayloadAction<number>) {
      state.customerId = action.payload
    },
    setDate(state, action: PayloadAction<string>) {
      state.date = action.payload
    },
    setNote(state, action: PayloadAction<string>) {
      state.note = action.payload
    },

    setPaymentMethod(state, action: PayloadAction<number>) {
      state.paymentMethod = action.payload
    },
    setPaymentStatus(state, action: PayloadAction<number>) {
      state.paymentStatus = action.payload
    },
    setSalesStatus(state, action: PayloadAction<number>) {
      state.salesStatus = action.payload
    },
    setCashReceived(state, action: PayloadAction<number>) {
      state.cashReceived = action.payload

      // change calculate
      state.change = state.cashReceived - state.totalAmount
    },
    setSplitBilling(state, action: PayloadAction<boolean>) {
      state.splitBilling = action.payload
      if (action.payload) {
        state.paymentMethod = null
      } else {
        state.paymentMethod = null
      }
    },
    setSplitPayments(
      state,
      action: PayloadAction<
        { id: number; amount: number; display_name: string }[]
      >
    ) {
      state.splitPayments = action.payload
    },
    resetOrder() {
      return {
        ...initialState,
        date: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      }
    },
  },
})

export const {
  addItem,
  removeItem,
  clearItems,
  updateItemQuantity,
  updateItemDiscount,
  updateItem,
  setGlobalDiscountAmount,
  setGlobalDiscountPercent,
  setPaymentMethod,
  setPaymentStatus,
  setSalesStatus,
  setCashReceived,
  setSplitBilling,
  setSplitPayments,
  setNote,
  resetOrder,
  updateSellPrice,
  setOrderForEdit,
  setCustomerId,
  setDate,
} = orderFormSlice.actions

export default orderFormSlice.reducer
